import { createSelector } from 'reselect'
import { EmptyArray, sortBy, sum } from 'lib/array/arrayUtils'
import { isCruiseItem } from 'lib/checkout/checkoutUtils'
import { isCheckoutReviewAndPaymentStepId, type CheckoutPageId } from 'checkout/constants/pages'
import { getCanRedeemLuxPlusBenefitsOnOrder, getIsEnabledLuxPlusDiscountedCruise, getIsLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import { getCurrentIntl } from 'i18n/util/currentIntl'
import { isSpoofed } from 'selectors/isSpoofed'
import {
  getSessionByCruiseItem,
  getCabinListByCruiseItem,
  getRateListByCruiseItem,
  getCabinDetailsByCruiseItem,
  getRateDetailsByCruiseItem,
  getCabinSelectionByCruiseItem,
  getCabinPricingByCruiseItem,
  getCabinReleaseByCruiseItem,
  getBookingCabinsByCategory,
  getBookingCabinTypes,
  buildCabinsWithPrice,
  getCabinLowestPrice,
  setDeckLowestPrice,
  getCruiseConsolidatedPaymentScheduleByBooking,
  getOverallCruiseConsolidatedPaymentSchedule,
  getCruiseHotelRecommendationFilters,
} from 'checkout/lib/utils/cruises/booking'
import { SKIP_STEP_FOR_CABIN_TYPES } from 'constants/cruise'
import { getCurrentCruiseCheckoutPage } from 'checkout/lib/utils/cruises/steps'
import { reselectShallowEqual } from 'lib/selector/selectorUtils'
import getOfferListKey from 'lib/offer/offerListKey'
import { getBedbankRateKey } from 'lib/hotels/getBedbankRateKey'

// Cart Data Selectors
export const getCruiseItems = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (items): Array<App.Checkout.CruiseItem> => items.filter(isCruiseItem),
  reselectShallowEqual,
)

export const getBookingSelectedCabinData = createSelector(
  (state: App.State) => getCruiseItems(state),
  (state: App.State) => state.router.location.pathname,
  (cruiseItems, pathname): App.Cruises.BookingSelectedCabinData => {
    const { selectedCabinItemId, isAllCabinsSelected } = getCurrentCruiseCheckoutPage(pathname, cruiseItems)
    return {
      selectedCabinItemId,
      isAllCabinsSelected,
    }
  },
)

export const getSelectedCruiseItem = createSelector(
  (state: App.State) => getBookingSelectedCabinData(state),
  (state: App.State) => getCruiseItems(state),
  (selectedCabinData, cruiseItems): App.Checkout.CruiseItem => {
    const cruiseItem = cruiseItems.find(item => item.itemId === selectedCabinData.selectedCabinItemId)
    return cruiseItem ?? cruiseItems[0]
  },
)

export const getPreviousCheckoutStepId = createSelector(
  (state: App.State) => getCruiseItems(state),
  (state: App.State) => state.routeHistory.prevPath,
  (cruiseItems, prevPath): CheckoutPageId => {
    const { currentStepId } = getCurrentCruiseCheckoutPage(prevPath, cruiseItems)
    return currentStepId
  },
)

// Checkout validation selectors
export const getIsCruiseCheckoutReady = createSelector(
  (state: App.State) => getCruiseItems(state),
  (state: App.State) => state.cruise.cruiseOffers,
  (state: App.State) => state.cruise.cruiseDeparture,
  (state: App.State) => state.cruise.multiBooking.cabinPricing,
  (state: App.State) => state.checkout.processing,
  (state: App.State) => state.checkout.processed,
  (cruiseItems, offers, departureMap, allCabinPricingData, checkoutProcessing, checkoutProcessed): boolean => {
    if (!cruiseItems.length) return false

    const { offerId, departureId } = cruiseItems[0]
    const offer = offers[offerId]
    const departure = departureMap[departureId]
    const isCabinPricingLoading = Object.values(allCabinPricingData).filter(Boolean).some(data => data.loading)

    return (
      // prevent user from changing step after the purchase is completed
      // prevent user from changing step while processing the purchase
      (!!offer &&
        !!departure &&
        !isCabinPricingLoading &&
        !checkoutProcessing && !checkoutProcessed)
    )
  },
)

// Offer Data Selectors
export const getBookingCruiseOffer = createSelector(
  (state: App.State) => getSelectedCruiseItem(state),
  (state: App.State) => state.cruise.cruiseOffers,
  (cruiseItem, offers): App.CruiseOffer => offers[cruiseItem?.offerId],
)

export const getBookingDepartures = createSelector(
  (state: App.State) => getBookingCruiseOffer(state),
  (offer): Array<App.CruiseDepartureDetails> => {
    return offer?.departures ? Object.values(offer.departures).flat() : EmptyArray
  },
)

export const getBookingDeparture = createSelector(
  (state: App.State) => getSelectedCruiseItem(state),
  (state: App.State) => state.cruise.cruiseDeparture,
  (cruiseItem, departureMap): App.CruiseDepartureView => {
    return departureMap[cruiseItem?.departureId]
  },
)

export const getBookingCabinPositions = createSelector(
  (state: App.State) => state.cruise.ships,
  (state: App.State) => getBookingCruiseOffer(state),
  (state: App.State) => getSelectedCruiseItem(state),
  (ships, offer, cruiseItem): Array<App.ShipCabinPosition> => {
    if (!ships || !offer || !cruiseItem?.deckId) return []
    const shipDetails = ships[offer.ship.id]
    return shipDetails?.decks.find(deck => deck.id === cruiseItem.deckId)?.shipCabinPosition ?? []
  },
)

export const getBookingCustomShipCategoryGroups = createSelector(
  (state: App.State) => getBookingCruiseOffer(state),
  (state: App.State) => state.cruise.ships,
  (offer, ships): Array<App.Cruises.ShipCabinCategoriesGroup> => {
    if (!offer) return EmptyArray
    return ships[offer.ship.id]?.cabinCategoriesGroups ?? EmptyArray
  },
)

/* BOOKING DATA - SELECTED CRUISE ITEM
 * In booking flow with multi cabins, the user will have one cabin (cruise item) selected at a time
 * These selectors are used to get the booking data (rates, cabins...) for the current selected cruise item.
 */
export const getBookingSessionData = createSelector(
  (state: App.State) => getSelectedCruiseItem(state),
  (state: App.State) => state.cruise.multiBooking.session,
  (cruiseItem, sessions): App.Cruises.BookingSessionData => {
    return getSessionByCruiseItem(cruiseItem, sessions)
  },
)

export const getBookingRateListData = createSelector(
  (state: App.State) => getSelectedCruiseItem(state),
  (state: App.State) => state.cruise.multiBooking.rateList,
  (cruiseItem, rateList): App.Cruises.CruiseBookingRateListDataWithStatus => {
    return getRateListByCruiseItem(cruiseItem, rateList)
  },
)

export const getBookingCabinListData = createSelector(
  (state: App.State) => getSelectedCruiseItem(state),
  (state: App.State) => state.cruise.multiBooking.cabinList,
  (cruiseItem, cabinList): App.Cruises.BookingCabinListDataWithStatus => {
    return getCabinListByCruiseItem(cruiseItem, cabinList)
  },
)

export const getBookingCabinDetailsData = createSelector(
  (state: App.State) => getSelectedCruiseItem(state),
  (state: App.State) => state.cruise.multiBooking.cabinDetails,
  (cruiseItem, cabinDetails): App.Cruises.BookingCabinDetailsListData => {
    return getCabinDetailsByCruiseItem(cruiseItem, cabinDetails)
  },
)

export const getBookingRateDetailsData = createSelector(
  (state: App.State) => getSelectedCruiseItem(state),
  (state: App.State) => state.cruise.multiBooking.rateDetails,
  (cruiseItem, rateDetails): App.Cruises.BookingRateDetailsData => {
    return getRateDetailsByCruiseItem(cruiseItem, rateDetails)
  },
)

export const getBookingCabinSelectionData = createSelector(
  (state: App.State) => getSelectedCruiseItem(state),
  (state: App.State) => state.cruise.multiBooking.cabinSelection,
  (cruiseItem, cabinSelection): App.Cruises.BookingCabinSelectionDataWithStatus => {
    return getCabinSelectionByCruiseItem(cruiseItem, cabinSelection)
  },
)

export const getBookingCabinPricingData = createSelector(
  (state: App.State) => getSelectedCruiseItem(state),
  (state: App.State) => state.cruise.multiBooking.cabinPricing,
  (cruiseItem, cabinPricing): App.Cruises.BookingCabinPricingDataWithStatus => {
    return getCabinPricingByCruiseItem(cruiseItem, cabinPricing)
  },
)

export const getBookingCabinReleaseData = createSelector(
  (state: App.State) => getSelectedCruiseItem(state),
  (state: App.State) => state.cruise.multiBooking.cabinRelease,
  (cruiseItem, cabinRelease): App.Cruises.BookingCabinReleaseData => {
    return getCabinReleaseByCruiseItem(cruiseItem, cabinRelease)
  },
)

export const getIsCabinPricingLoading = createSelector(
  (state: App.State) => getCruiseItems(state),
  (state: App.State) => state.cruise.multiBooking.cabinPricing,
  (cruiseItems, cruiseCabinPricingMap): boolean => {
    return cruiseItems.some((item) => {
      const { loading } = getCabinPricingByCruiseItem(item, cruiseCabinPricingMap)
      return loading
    })
  },
)

export const getHasOnlyOneCabinType = createSelector(
  (state: App.State) => getBookingCruiseOffer(state),
  (state: App.State) => getCruiseItems(state),
  (state: App.State) => state.cruise.multiBooking.rateList,
  (offer, cruiseItems, rateListMap): boolean => {
    const hasOnlyOneCabinType = cruiseItems
      .every((item) => {
        const rateListData = getRateListByCruiseItem(item, rateListMap)
        const cabinTypes = getBookingCabinTypes(rateListData, offer)

        const cabinTypesAvailable = cabinTypes.filter(cabinType => !!cabinType.lowestRatePrice)
        return cabinTypesAvailable.length === 1 && SKIP_STEP_FOR_CABIN_TYPES.includes(cabinTypesAvailable[0].name)
      })

    return hasOnlyOneCabinType
  },
)

export const getBookingAllCabinsWithPrice = createSelector(
  (state: App.State) => getBookingCruiseOffer(state),
  (state: App.State) => getBookingCabinListData(state),
  (state: App.State) => getBookingRateListData(state),
  (state: App.State) => getBookingCabinDetailsData(state),
  (state: App.State) => getSelectedCruiseItem(state),
  (state: App.State) => getCruiseItems(state),
  (offer, cabinLisData, rateListData, cabinDetails, cruiseItem, cruiseItems): Array<App.Cruises.BookingCabinWithPrice> => {
    if (!offer || !cruiseItem) return []

    const selectedRates = rateListData.rates.filter(rate => (
      rate.cabinType === cruiseItem.cabinType
    ))

    return buildCabinsWithPrice(
      cruiseItem,
      cruiseItems,
      cabinLisData,
      cabinDetails,
      selectedRates,
    )
  },
)

export const getBookingCabinsWithPrice = createSelector(
  (state: App.State) => getBookingCruiseOffer(state),
  (state: App.State) => getBookingCabinListData(state),
  (state: App.State) => getBookingRateListData(state),
  (state: App.State) => getBookingCabinDetailsData(state),
  (state: App.State) => getSelectedCruiseItem(state),
  (state: App.State) => getCruiseItems(state),
  (offer, cabinLisData, rateListData, cabinDetails, cruiseItem, cruiseItems): Array<App.Cruises.BookingCabinWithPrice> => {
    if (!offer || !cruiseItem) return []

    const cabinCodes = cruiseItem.cabinCodes || []

    const selectedRates = rateListData.rates.filter(rate => (
      rate.cabinType === cruiseItem.cabinType &&
      cabinCodes.includes(rate.pricedCategoryCode)
    ))

    return buildCabinsWithPrice(
      cruiseItem,
      cruiseItems,
      cabinLisData,
      cabinDetails,
      selectedRates,
    )
  },
)

export const getBookingDecksAvailable = createSelector(
  (state: App.State) => getBookingCruiseOffer(state),
  (state: App.State) => getBookingCabinsWithPrice(state),
  (offer, cabinsWithPrice): Array<App.CruiseDeck> => {
    const intl = getCurrentIntl()
    const deckFallbackLabel = intl.formatMessage({
      id: 'VS7ieAnuKsCliM6MdKaW',
      defaultMessage: 'Deck',
      description: 'Fallback label for cruise deck when name is not available',
    })
    // We want to display the decks even if there are no cabins
    // this reduce also removes duplicate decks that have the same name
    const decksByName = offer.ship.decks?.reduce<Record<string, App.CruiseDeck>>((acc, deck) => {
      const cabins = cabinsWithPrice.filter(cabin => (
        cabin.deckId === deck.id &&
        !cabin.isGuaranteed
      ))

      const groupedDeck = acc[deck.name]
      if (groupedDeck && !!groupedDeck.cabinAmount) {
        return acc
      }

      const cabinLowestPrice = getCabinLowestPrice(cabins)

      return {
        ...acc,
        [deck.name]: {
          id: deck.id,
          deckId: String(deck.externalId),
          deckName: deck.name || deckFallbackLabel,
          deckNumber: deck.deckNumber,
          image: deck.imageId ?? '',
          shipCabinPositionList: deck.shipCabinPosition ?? [],
          cabinAmount: cabins.length,
          isDeckWithLowestPrice: false,
          cabinLowestPrice: cabinLowestPrice?.price,
          cabinCategoryWithLowestPrice: cabinLowestPrice?.cabinCategory,
        },
      }
    }, {}) ?? {}

    const decks = setDeckLowestPrice(Object.values(decksByName))

    return sortBy(decks, (deck) => deck.deckNumber, 'desc')
  },
)

export const getBookingCabinCategories = createSelector(
  (state: App.State) => getBookingRateListData(state),
  (state: App.State) => getBookingAllCabinsWithPrice(state),
  (state: App.State) => getSelectedCruiseItem(state),
  (state: App.State) => getBookingCruiseOffer(state),
  (state: App.State) => getBookingCustomShipCategoryGroups(state),
  (rateListData, cabinsWithPrice, cruiseItem, offer, customCabinCategoriesGroups): Array<App.Cruises.BookingCabinCategory> => {
    if (!offer || !cruiseItem) return []

    const categories = offer.ship.cabinCategories ?? []
    const cabinsByCategory = getBookingCabinsByCategory(cabinsWithPrice)

    const categoriesWithPrice = categories.map((category) => {
      const customCategoryGroup = customCabinCategoriesGroups.find(
        (cabinCategoryGroup) => cabinCategoryGroup.cabinCategories?.some(cabinCategory => cabinCategory.code === category.code),
      )

      const categoryRates = rateListData.rates.filter((rate) => (
        rate.pricedCategoryCode === category.code &&
        rate.cabinType === cruiseItem.cabinType
      ))

      const [lowestRateItem] = categoryRates
      const lowestRatePrice = lowestRateItem?.priceDetails

      const cabins = cabinsByCategory[category.code] ?? []
      const cabinsCount = cabins.filter(cabin => !cabin.isGuaranteed).length
      const [cabin] = cabins

      const luxPlusInclusionsByTier = categoryRates.find(rate => rate.luxPlusInclusionsByTier)?.luxPlusInclusionsByTier

      const hasGuaranteedCabin = cabins.some(cabin => cabin.isGuaranteed)
      const hasAccessibleCabin = cabins.some(cabin => cabin.isAccessibleCabin)
      const hasConnectingCabin = cabins.some(cabin => cabin.connectingCabinAvailable)

      const categoryImageIds = customCategoryGroup?.images?.map(image => image.imageId) ?? []
      const imageIds = categoryImageIds.length ? categoryImageIds : [category.imageId]
      const images = imageIds.filter(Boolean).map(imageId => ({ id: imageId }))

      const categoryInfo = category.cabinCategoryInfo?.[0]

      const name = categoryInfo?.name ?? category.cabinType
      const description = customCategoryGroup?.description ?? categoryInfo?.description
      const color = category.color || ''

      return {
        ...category,
        color,
        images,
        name,
        description,
        cabin,
        cabinsCount,
        luxPlusInclusionsByTier,
        lowestRatePrice,
        hasAccessibleCabin,
        hasConnectingCabin,
        hasGuaranteedCabin,
      }
    })
      .filter((category) => category.lowestRatePrice)

    const parsedCategories = categoriesWithPrice.map((category) => {
      const name = category.name
      const groupingKeywordList = ['Guarantee', 'Some Accessible', 'Accessible', 'Interconnecting', 'Connecting']
      const keyword = groupingKeywordList.find((group) => category.name.includes(group))

      if (keyword) {
        const parsedName = name
          .replaceAll(keyword ?? '', '') // Remove keyword occurrence
          .replace(/\(\s*\)/g, '') // Remove empty parentheses
          .replace(/-\s*$/, '') // Remove trailing hyphen
          .replace(/^\s*-\s*/, '') // Remove leading hyphen
          .trim()

        return {
          ...category,
          name: parsedName,
        }
      }

      return category
    })

    return sortBy(
      parsedCategories,
      (category) => category.lowestRatePrice.price, 'asc',
    )
  },
)

export const getCurrentCheckoutStepId = createSelector(
  (state: App.State) => getCruiseItems(state),
  (state: App.State) => state.router.location.pathname,
  (cruiseItems, pathname): CheckoutPageId => {
    const { currentStepId } = getCurrentCruiseCheckoutPage(pathname, cruiseItems)
    return currentStepId
  },
)

// Cruise summary selectors
export const showCruiseSummaryTotals = createSelector(
  (state: App.State) => getCurrentCheckoutStepId(state),
  (state: App.State) => getSelectedCruiseItem(state),
  (state: App.State) => getBookingCabinSelectionData(state),
  (currentCheckoutPage, isCruiseCheckout, cabinSelectionData) => {
    return (
      !isCruiseCheckout ||
      (
        isCheckoutReviewAndPaymentStepId(currentCheckoutPage) &&
        !cabinSelectionData.loading
      )
    )
  },
)

// Price change modal selectors
export const getBookingCabinsWithPriceChanges = createSelector(
  (state: App.State) => getCruiseItems(state),
  (state: App.State) => state.cruise.multiBooking.cabinSelection,
  (state: App.State) => state.cruise.multiBooking.cabinPricing,
  (cruiseItems, cabinSelectionMap, cabinPricingMap): Array<App.Cruises.CabinWithPriceChanges> => {
    const intl = getCurrentIntl()
    const cabins: Array<App.Cruises.CabinWithPriceChanges> = []

    // We won't show the modal until all cabin prices are loaded and without errors
    const isAllCabinPricingLoaded = cruiseItems.every(item => {
      const cabinPrice = cabinPricingMap[item.itemId]
      return !!cabinPrice && !cabinPrice.error && !cabinPrice.loading
    })
    if (!isAllCabinPricingLoaded) {
      return cabins
    }

    cruiseItems.map((cruiseItem, index) => {
      const { cabinSelection } = getCabinSelectionByCruiseItem(cruiseItem, cabinSelectionMap)
      const { cabinPricing } = getCabinPricingByCruiseItem(cruiseItem, cabinPricingMap)

      const oldPriceAmount = cabinSelection?.pricing.total.amount ?? 0
      const newPriceAmount = cabinPricing?.pricing.total.amount ?? 0
      const isChanged = cabinSelection && cabinPricing && oldPriceAmount !== newPriceAmount

      if (isChanged) {
        cabins.push({
          name: intl.formatMessage(
            {
              id: 'CxKB02orulRYd9GfsWa5',
              defaultMessage: 'Cabin {number}',
              description: 'Cabin number label for price change modal in cabin selection',
            },
            { number: index + 1 },
          ),
          oldPriceAmount,
          newPriceAmount,
          currency: cabinPricing?.pricing.currency,
        })
      }
    })

    if (cabins.length > 1) {
      const oldPriceTotalAmount = sum(cabins, cabin => cabin.oldPriceAmount)
      const newPriceTotalAmount = sum(cabins, cabin => cabin.newPriceAmount)
      const currency = cabins[0].currency

      cabins.push({
        name: intl.formatMessage({
          id: 'gmR8YDYdJJ2NqaVfb8E-',
          defaultMessage: 'Total',
          description: 'Total label for cruise price change modal in the cruise section',
        }),
        oldPriceAmount: oldPriceTotalAmount,
        newPriceAmount: newPriceTotalAmount,
        currency,
      })
    }

    return cabins
  },
)

export const hasMultipleCabins = createSelector(
  (state: App.State) => getCruiseItems(state),
  (cruiseItems): boolean => cruiseItems.length > 1,
)

export const getOverallConsolidatedPaymentSchedule = createSelector(
  (state: App.State) => state.cruise.multiBooking.consolidatedPaymentSchedule,
  (consolidatedPaymentSchedule): App.Cruises.CruisePaymentSchedule | undefined => {
    return getOverallCruiseConsolidatedPaymentSchedule(consolidatedPaymentSchedule.schedule)
  },
)

export const getConsolidatedPaymentScheduleByBooking = createSelector(
  (state: App.State) => getSelectedCruiseItem(state),
  (state: App.State) => state.cruise.multiBooking.consolidatedPaymentSchedule,
  (cruiseItem, consolidatedPaymentSchedule): App.Cruises.CruisePaymentSchedule | undefined => {
    return getCruiseConsolidatedPaymentScheduleByBooking(consolidatedPaymentSchedule.schedule, cruiseItem.bookingId)
  },
)

export const isFetchingCruiseConsolidatedPaymentSchedule = createSelector(
  (state: App.State) => state.cruise.multiBooking.cabinPricing,
  (state: App.State) => state.cruise.multiBooking.consolidatedPaymentSchedule,
  (cabinPricing, consolidatedPaymentSchedule): boolean => {
    if (!cabinPricing || !consolidatedPaymentSchedule) return false
    const isLoadingSomePricingItem = Object.values(cabinPricing).some(data => data?.loading)
    return isLoadingSomePricingItem || consolidatedPaymentSchedule.loading
  },
)

// Return if there is any package with LuxPlus inclusions in some of the cabins
export const hasLuxPlusInclusionsInCruiseBooking = createSelector(
  (state: App.State) => getCruiseItems(state),
  (state: App.State) => getBookingCruiseOffer(state),
  (state: App.State) => getBookingDeparture(state),
  (state: App.State) => getIsLuxPlusEnabled(state),
  (state: App.State) => state.cruise.multiBooking.rateList,
  (cruiseItems, offer, departure, isLuxPlusEnabled, rateList): boolean => {
    if (!offer || !departure || !isLuxPlusEnabled) return false

    return cruiseItems.some(cruiseItem => {
      const rateListData = getRateListByCruiseItem(cruiseItem, rateList)
      return rateListData.rates.some(rate => rate.luxPlusInclusionsByTier)
    })
  },
)

function hasOfferWithRates(
  offerId: string,
  bedbankOfferRates: App.State['offer']['bedbankOfferRates'],
  filters: App.OfferListFilters,
): boolean {
  if (!filters.checkIn || !filters.checkOut || !filters.rooms) return false
  const rateKey = getBedbankRateKey(filters.rooms, filters.checkIn, filters.checkOut)
  const rates = bedbankOfferRates[offerId]?.[rateKey]
  return !!rates && rates.length > 0
}

/**
 * Checks if hotel recommendations are available for the cruise checkout
 * Hotel recommendations are only available for single-cabin, non-bundled offers.
 */
export const hasCruiseHotelRecommendationsAvailable = createSelector(
  (state: App.State) => hasMultipleCabins(state),
  (state: App.State) => getSelectedCruiseItem(state),
  (state: App.State) => getBookingCruiseOffer(state),
  (state: App.State) => state.offer.offerLists,
  (state: App.State) => state.cruise.offerPlaces,
  (state: App.State) => state.offer.bedbankOfferRates,
  (hasMultipleCabins, cruiseItem, cruiseOffer, offerLists, offerPlaces, bedbankOfferRates): boolean => {
    if (!cruiseItem || !cruiseOffer || hasMultipleCabins || !!cruiseOffer.bundle) return false

    const places = offerPlaces[cruiseOffer.id]
    if (!places?.departurePlace || !places?.returnPlace) return false

    let hasBeforeOffers = false
    let hasAfterOffers = false

    const filtersBefore = getCruiseHotelRecommendationFilters('before', cruiseItem, 1, places.departurePlace.id)
    if (filtersBefore) {
      const keyBefore = getOfferListKey(filtersBefore)
      const beforeOfferIds = offerLists[keyBefore]?.offerIds ?? []
      hasBeforeOffers = beforeOfferIds.some(id => hasOfferWithRates(id, bedbankOfferRates, filtersBefore))
    }

    const filtersAfter = getCruiseHotelRecommendationFilters('after', cruiseItem, 1, places.returnPlace.id)
    if (filtersAfter) {
      const keyAfter = getOfferListKey(filtersAfter)
      const afterOfferIds = offerLists[keyAfter]?.offerIds ?? []
      hasAfterOffers = afterOfferIds.some(id => hasOfferWithRates(id, bedbankOfferRates, filtersAfter))
    }

    return hasBeforeOffers || hasAfterOffers
  },
)

/**
 * Agents can see LuxPlus price in spoofed mode (member redeem or spoof).
 */
export const getCanSeeLuxPlusPrice = createSelector(
  (state: App.State) => getIsEnabledLuxPlusDiscountedCruise(state),
  (state: App.State) => getCanRedeemLuxPlusBenefitsOnOrder(state),
  (state: App.State) => isSpoofed(state),
  (_state: App.State, hasMemberPrice: boolean) => hasMemberPrice,
  (isLuxPlusEnabled, canRedeemLuxPlusBenefitsOnOrder, isSpoofed, hasMemberPrice): boolean => {
    const canSeeSession = canRedeemLuxPlusBenefitsOnOrder || isSpoofed
    return isLuxPlusEnabled && hasMemberPrice && canSeeSession
  },
)

/**
 * Display label when there is member pricing and the session cannot see member prices
 */
export const getCanSeeLuxPlusLabel = createSelector(
  (state: App.State) => getIsEnabledLuxPlusDiscountedCruise(state),
  (state: App.State) => getCanRedeemLuxPlusBenefitsOnOrder(state),
  (state: App.State) => isSpoofed(state),
  (_state: App.State, hasMemberPrice: boolean) => hasMemberPrice,
  (
    _state: App.State,
    _hasMemberPrice: boolean,
    lowestDepartureMemberPrice?: App.Cruises.CruiseOfferMemberPriceByCategory,
  ) => lowestDepartureMemberPrice,
  (isLuxPlusEnabled, canRedeemLuxPlusBenefitsOnOrder, isSpoofed, hasMemberPrice, lowestDepartureMemberPrice): boolean => {
    const canSeeSession = canRedeemLuxPlusBenefitsOnOrder || isSpoofed

    if (
      isLuxPlusEnabled &&
      hasMemberPrice &&
      !lowestDepartureMemberPrice?.isMemberPricePublic
    ) {
      return true
    }

    if (isLuxPlusEnabled && hasMemberPrice && !canSeeSession) {
      return true
    }

    return false
  },
)
